import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { LoggingService, LogLevel } from './logging.service';

export interface QueueMessage {
  id: string;
  channel: string;
  data: any;
  timestamp: number;
  retryCount?: number;
}

export interface QueueConfig {
  maxSize: number;
  ttl: number;
  policy: 'drop-oldest' | 'drop-newest' | 'reject';
  batchSize: number;
}

export interface QueueStatus {
  size: number;
  oldestAge: number;
  newestAge: number;
  droppedCount: number;
  rejectedCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class MessageQueueService {
  private queue: QueueMessage[] = [];
  private config: QueueConfig = {
    maxSize: 100,
    ttl: 30000,
    policy: 'drop-oldest',
    batchSize: 10
  };
  
  private droppedCount = 0;
  private rejectedCount = 0;

  // Event emitters
  private queueOverflowSubject = new Subject<{
    policy: string;
    droppedMessage: QueueMessage;
    timestamp: number;
  }>();

  private messageTtlExpiredSubject = new Subject<{
    expiredCount: number;
    oldestAge: number;
    timestamp: number;
  }>();

  constructor(private loggingService: LoggingService) {}

  configure(config: Partial<QueueConfig>): void {
    this.config = { ...this.config, ...config };
  }

  // Add message to queue
  enqueue(message: QueueMessage): boolean {
    // Clean expired messages first
    this.cleanExpiredMessages();

    // Check if queue is full
    if (this.queue.length >= this.config.maxSize) {
      return this.handleQueueFull(message);
    }

    this.queue.push(message);
    return true;
  }

  // Get next batch of messages
  dequeue(batchSize?: number): QueueMessage[] {
    // Clean expired messages first
    this.cleanExpiredMessages();

    const size = batchSize || this.config.batchSize;
    return this.queue.splice(0, Math.min(size, this.queue.length));
  }

  // Peek at next messages without removing
  peek(count: number = 1): QueueMessage[] {
    this.cleanExpiredMessages();
    return this.queue.slice(0, count);
  }

  // Get queue status
  getStatus(): QueueStatus {
    this.cleanExpiredMessages();
    
    const now = Date.now();
    const ages = this.queue.map(msg => now - msg.timestamp);
    
    return {
      size: this.queue.length,
      oldestAge: ages.length > 0 ? Math.max(...ages) : 0,
      newestAge: ages.length > 0 ? Math.min(...ages) : 0,
      droppedCount: this.droppedCount,
      rejectedCount: this.rejectedCount
    };
  }

  // Check if queue is empty
  isEmpty(): boolean {
    this.cleanExpiredMessages();
    return this.queue.length === 0;
  }

  // Get current size
  size(): number {
    this.cleanExpiredMessages();
    return this.queue.length;
  }

  // Clear all messages
  clear(): void {
    const clearedCount = this.queue.length;
    this.queue = [];
    
    if (clearedCount > 0) {
      this.loggingService.log(LogLevel.WARN, 'MessageQueueService',
        `Cleared ${clearedCount} messages from queue`);
    }
  }

  // Observable for queue overflow events
  get queueOverflow$() {
    return this.queueOverflowSubject.asObservable();
  }

  // Observable for TTL expiry events
  get messageTtlExpired$() {
    return this.messageTtlExpiredSubject.asObservable();
  }

  // Reset statistics
  resetStats(): void {
    this.droppedCount = 0;
    this.rejectedCount = 0;
  }

  // Cleanup resources
  destroy(): void {
    this.clear();
    this.queueOverflowSubject.complete();
    this.messageTtlExpiredSubject.complete();
  }

  private cleanExpiredMessages(): void {
    const now = Date.now();
    const beforeCount = this.queue.length;
    
    this.queue = this.queue.filter(msg => (now - msg.timestamp) < this.config.ttl);
    
    const expiredCount = beforeCount - this.queue.length;
    if (expiredCount > 0) {
      const oldestAge = this.config.ttl;
      
      this.loggingService.log(LogLevel.DEBUG, 'MessageQueueService',
        `Removed ${expiredCount} expired messages (TTL: ${this.config.ttl}ms)`);
      
      this.messageTtlExpiredSubject.next({
        expiredCount,
        oldestAge,
        timestamp: now
      });
    }
  }

  private handleQueueFull(message: QueueMessage): boolean {
    switch (this.config.policy) {
      case 'drop-oldest':
        const droppedOldest = this.queue.shift();
        this.queue.push(message);
        this.droppedCount++;
        
        if (droppedOldest) {
          this.loggingService.log(LogLevel.WARN, 'MessageQueueService',
            `Dropped oldest message (age: ${Date.now() - droppedOldest.timestamp}ms) to make room`);
          
          this.queueOverflowSubject.next({
            policy: 'drop-oldest',
            droppedMessage: droppedOldest,
            timestamp: Date.now()
          });
        }
        return true;

      case 'drop-newest':
        this.droppedCount++;
        this.loggingService.log(LogLevel.WARN, 'MessageQueueService',
          'Dropped newest message due to full queue');
        
        this.queueOverflowSubject.next({
          policy: 'drop-newest',
          droppedMessage: message,
          timestamp: Date.now()
        });
        return false;

      case 'reject':
        this.rejectedCount++;
        this.loggingService.log(LogLevel.ERROR, 'MessageQueueService',
          'Rejected message due to full queue');
        return false;

      default:
        return false;
    }
  }
}
